import React, { useState, useEffect, useRef } from 'react';
import './NewCoupon.css';
import Select from "react-select";
import { Link, useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import Swal from 'sweetalert2';
import { buildDiffPreview, fireAudit } from '../utils/auditClient';

const NewCoupon = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const editId = queryParams.get("couponId");
  const isEditMode = !!editId;

  const token = localStorage.getItem('auth-token');
  const userData = JSON.parse(localStorage.getItem('userData'));
  const userRoles = userData?.roles;
  const isSuperAdmin = userRoles && userRoles.length > 0 && userRoles.some((role) => role === 'super admin');

  const [isHidden, setIsHidden] = useState(true);
  const [forAdminsOnly, setForAdminsOnly] = useState(false);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [price, setPrice] = useState('');
  const [mobileNumbers, setMobileNumbers] = useState([]);
  const [selectedScope, setSelectedScope] = useState([]);
  const [selectedAcademy, setSelectedAcademy] = useState([]);
  const [existingcoupon, setExistingcoupon] = useState([]);
  const [minOrderAmount, setMinOrderAmount] = useState('');
  const [maxDiscount, setMaxDiscount] = useState('');
  const [percentage, setPercentage] = useState('');
  const [maxUsagePerUser, setMaxUsagePerUser] = useState('');
  const [couponCode, setCouponCode] = useState('');
  const [startTime, setStartTime] = useState('');
  const [endTime, setEndTime] = useState('');
  const [forAllUsers, setForAllUsers] = useState('none');
  const [selectedSport, setSelectedSport] = useState([]);

  const [scopeOptions, setScopeOptions] = useState([]);
  const [academyOptions, setAcademyOptions] = useState([]);
  const [ExistingCouponOptions, setExistingCouponOptions] = useState([]);
  const [sportOptions, setSportOptions] = useState([]);

  const baselineRef = useRef(null);
  const initializedBaselineRef = useRef(false);

  const [optionsLoaded, setOptionsLoaded] = useState(false);
  const [editLoaded, setEditLoaded] = useState(false);

  const buildSubmitPayload = () => {
    const sportIds = selectedSport?.length ? selectedSport.map(s => s.value) : [];
    return {
      id: isEditMode ? editId : null,
      title: title || '',
      description: description || '',
      price: price || '',
      academyIds: selectedAcademy?.length ? selectedAcademy.map(a => a.value) : null,
      selections: existingcoupon?.length ? existingcoupon.map(c => c.value) : null,
      mobileNumbers: mobileNumbers || [],
      scopes: selectedScope?.length ? selectedScope.map(s => s.value) : [],
      minAmount: minOrderAmount === '' ? null : parseFloat(minOrderAmount),
      maxAmount: maxDiscount === '' ? null : parseFloat(maxDiscount),
      percentage: percentage === '' ? null : parseFloat(percentage),
      maxUsagePerUser: maxUsagePerUser === '' ? null : Number(maxUsagePerUser),
      couponCode: couponCode || '',
      startTime: startTime || '',
      endTime: endTime || '',
      isForAllUsers: forAllUsers === 'yes',
      isForAllUsersOption: forAllUsers === 'yes' ? 'YES' : 'NO',
      sportIds,
      isHidden: !!isHidden,
      forAdminsOnly: !!forAdminsOnly
    };
  };

  const redactForAudit = (payload) => {
    if (!payload) return payload;
    const copy = { ...payload };
    const maskLast4 = (num) => (typeof num === 'string' ? num.replace(/\d(?=\d{4})/g, '*') : num);
    if (Array.isArray(copy.mobileNumbers)) {
      copy.mobileNumbers = copy.mobileNumbers.map(maskLast4);
    }
    if (typeof copy.couponCode === 'string' && copy.couponCode.length) {
      copy.couponCode = copy.couponCode.replace(/.(?=.{2}$)/g, '*');
    }
    return copy;
  };

  const fetchCouponOptions = async () => {
    try {
      const response = await fetch(process.env.REACT_APP_API_COUPONS_NEW, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) throw new Error(`Failed to fetch data: ${response.status} ${response.statusText}`);

      const { data } = await response.json();
      if (!data) throw new Error('Data is undefined or null');

      const newScopeOptions = data.couponScope.map(scope => ({
        value: scope,
        label: scope.replace('_', ' ').replace(/\b\w/g, c => c.toUpperCase()),
      }));

      const newExistingCouponOptions = data.couponSelection.map(coupon => ({
        value: coupon,
        label: coupon.replace('_', ' ').replace(/\b\w/g, c => c.toUpperCase()),
      }));

      const academiesArray = Object.keys(data.academies).map(key => ({
        id: key,
        name: data.academies[key],
      }));

      const sportsArray = data.sports?.map(sport => ({
        value: sport.id.toString(),
        label: sport.name,
      })) || [];

      const newAcademyOptions = academiesArray.map(academy => ({
        value: academy.id,
        label: academy.name,
      }));

      setSportOptions(sportsArray);
      setScopeOptions(newScopeOptions);
      setAcademyOptions(newAcademyOptions);
      setExistingCouponOptions(newExistingCouponOptions);
      setOptionsLoaded(true);
    } catch (error) {
    }
  };

  const fetchEditCoupon = async () => {
    try {
      const res = await axios.get(`${process.env.REACT_APP_API_COUPONS_UPDATE}?coupon_id=${editId}`, {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
      });

      if (res.data?.success) {
        const couponData = res.data.data.couponForm;

        setTitle(couponData.title || '');
        setDescription(couponData.description || '');
        setMinOrderAmount(couponData.minAmount ?? '');
        setMaxDiscount(couponData.maxAmount ?? '');
        setPercentage(couponData.percentage ?? '');
        setMaxUsagePerUser(couponData.maxUsagePerUser ?? '');
        setCouponCode(couponData.couponCode || '');
        setStartTime(couponData.startTime ? couponData.startTime.split('T')[0] : '');
        setEndTime(couponData.endTime ? couponData.endTime.split('T')[0] : '');
        setForAllUsers(couponData.isForAllUsers ? 'yes' : 'no');
        setIsHidden(couponData.isHidden ?? true);

        setSelectedScope(
          couponData.scopes?.map(scope => ({
            value: scope,
            label: scope.replace('_', ' ').replace(/\b\w/g, ch => ch.toUpperCase()),
          })) || []
        );

        const matchedAcademies =
          couponData.academyIds?.map(id => {
            const academy = academyOptions.find(opt => opt.value === id.toString());
            return academy ? { value: academy.value, label: academy.label } : null;
          }).filter(Boolean) || [];
        setSelectedAcademy(matchedAcademies);

        setExistingcoupon(
          couponData.selections?.map(selection => ({
            value: selection,
            label: selection.replace('_', ' ').replace(/\b\w/g, ch => ch.toUpperCase()),
          })) || []
        );

        const matchedSports =
          couponData.sportIds?.map(id => {
            const sport = sportOptions.find(opt => opt.value === id.toString());
            return sport ? { value: sport.value, label: sport.label } : null;
          }).filter(Boolean) || [];
        setSelectedSport(matchedSports);

        const cleanMobileNumbers = (couponData.mobileNumbers || ['']).map(num =>
          typeof num === 'string' ? num.replace(/^(\+91|0)/, '') : num
        );
        setMobileNumbers(cleanMobileNumbers);

        setEditLoaded(true);
      }
    } catch (error) {
    }
  };

  useEffect(() => {
    fetchCouponOptions();
  }, []);

  useEffect(() => {
    if (isEditMode && optionsLoaded) {
      fetchEditCoupon();
    }
  }, [isEditMode, optionsLoaded]);

  useEffect(() => {
    if (initializedBaselineRef.current) return;
    const readyForBaseline = isEditMode ? editLoaded : true;
    if (readyForBaseline) {
      baselineRef.current = buildSubmitPayload();
      initializedBaselineRef.current = true;
    }
  }, [isEditMode, editLoaded, title, description, couponCode, selectedScope.length, selectedAcademy.length, selectedSport.length]);

  const handleAddMobileNumber = () => setMobileNumbers([...mobileNumbers, '']);
  const handleScopeChange = (selected) => setSelectedScope(selected || []);
  const handleAcademyChange = (selected) => setSelectedAcademy(selected || []);
  const handleExistingCoupon = (selected) => setExistingcoupon(selected || []);
  const handleSportChange = (selected) => setSelectedSport(selected || []);
  const handleMobileNumberChange = (index, value) => {
    const updated = [...mobileNumbers];
    updated[index] = value.replace(/\D/g, '');
    setMobileNumbers(updated);
  };
  const handleRemoveMobileNumber = (index) => setMobileNumbers(mobileNumbers.filter((_, i) => i !== index));

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!selectedScope?.length) {
      Swal.fire({ icon: "warning", title: "Missing Scope", text: "Please select at least one Scope before submitting.", toast: true, position: "top-end", showConfirmButton: false, timer: 3000 });
      return;
    }
    if (!selectedSport?.length) {
      Swal.fire({ icon: "warning", title: "Missing Sport", text: "Please select at least one Sport before submitting.", toast: true, position: "top-end", showConfirmButton: false, timer: 3000 });
      return;
    }
    if (!selectedAcademy?.length) {
      Swal.fire({ icon: "warning", title: "Missing Academy", text: "Please select at least one Academy before submitting.", toast: true, position: "top-end", showConfirmButton: false, timer: 3000 });
      return;
    }
    if (forAllUsers === 'none') {
      Swal.fire({ icon: "warning", title: "Missing Option", text: "Please choose if this applies to all users.", toast: true, position: "top-end", showConfirmButton: false, timer: 3000 });
      return;
    }

    const apiUrl = process.env.REACT_APP_API_COUPONS_NEW;

    const newPayload = buildSubmitPayload();
    const oldPayload = baselineRef.current || {};

    if (isEditMode) {
      const { digest, html } = buildDiffPreview(redactForAudit(oldPayload), redactForAudit(newPayload));
      if (!digest.length) {
        Swal.fire({ icon: 'info', title: 'Nothing to update', text: 'No differences detected.', toast: true, position: 'top-end', showConfirmButton: false, timer: 2500 });
        return;
      }
      const confirm = await Swal.fire({ title: 'Apply these changes?', html, showCancelButton: true, confirmButtonText: 'Update' });
      if (!confirm.isConfirmed) return;
    }

    try {
      const headers = { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` };
      const response = await axios.post(apiUrl, newPayload, { headers });

      Swal.fire({
        icon: "success",
        title: "Success",
        text: response.data?.message || (isEditMode ? "Coupon updated successfully!" : "Coupon created successfully!"),
        toast: true,
        position: "top-end",
        showConfirmButton: false,
        timer: 3000,
      });

      const createdOrUpdatedId =
        response.data?.data?.couponId ||
        response.data?.data?.id ||
        editId ||
        null;

      fireAudit({
        entity: 'coupon',
        affected_id: createdOrUpdatedId,
        action: isEditMode ? 'update' : 'create',
        businessApi: apiUrl,
        businessMethod: 'POST',
        oldPayload: redactForAudit(oldPayload),
        newPayload: redactForAudit(newPayload),
        section: 'Coupons',
        subSection: 'New Coupon',
        extra: { page: 'NewCoupon' },
      });

      baselineRef.current = newPayload;

      navigate('/coupon');
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: error?.response?.data?.data?.error || "There was an error submitting the form!",
        toast: true,
        position: "top-end",
        showConfirmButton: false,
        timer: 3000,
      });
    }
  };

  return (
    <div className="enrollment-container">
      <div className="breadcrumb">
        <Link to="/coupon" className="breadcrumb-link">Coupon List</Link> &gt; {isEditMode ? 'Edit Coupon' : 'New Coupon'}
      </div>

      <form className="academy-form" onSubmit={handleSubmit}>
        <div className="floating-select-form" style={{ width: '98%' }}>
          <input
            type="text"
            name="title"
            className={`walkin-form ${title ? 'has-value' : ''}`}
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Enter Title"
            required
          />
          {title && <label htmlFor="title">Title</label>}
        </div>

        <div className="floating-select-form" style={{ width: '98%' }}>
          <textarea
            name="description"
            className={`walkin-form-comments ${description ? 'has-value' : ''}`}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Enter Description"
            rows={4}
            required
          />
          {description && <label htmlFor="description">Description</label>}
        </div>

        <div className="floating-select-form" style={{ width: '98%' }}>
          <input
            type="number"
            name="minOrderAmount"
            className={`walkin-form ${minOrderAmount ? 'has-value' : ''}`}
            value={minOrderAmount}
            onChange={(e) => setMinOrderAmount(e.target.value)}
            placeholder="Enter Minimum Order Amount"
            required
            onWheel={(e) => e.target.blur()}
            onKeyDown={(e) => ['ArrowUp', 'ArrowDown'].includes(e.key) && e.preventDefault()}
          />
          {minOrderAmount && <label htmlFor="minOrderAmount">Minimum Order Amount</label>}
        </div>

        <div className="floating-select-form" style={{ width: '98%' }}>
          <input
            type="number"
            name="maxDiscount"
            className={`walkin-form ${maxDiscount ? 'has-value' : ''}`}
            value={maxDiscount}
            onChange={(e) => setMaxDiscount(e.target.value)}
            placeholder="Enter Maximum Discount Amount"
            required
            onWheel={(e) => e.target.blur()}
            onKeyDown={(e) => ['ArrowUp', 'ArrowDown'].includes(e.key) && e.preventDefault()}
          />
          {maxDiscount && <label htmlFor="maxDiscount">Maximum Discount Amount</label>}
        </div>

        <div className="floating-select-form" style={{ width: '98%' }}>
          <input
            type="number"
            name="percentage"
            className={`walkin-form ${percentage ? 'has-value' : ''}`}
            value={percentage}
            onChange={(e) => setPercentage(e.target.value)}
            placeholder="Enter Percentage"
            required
            min="0"
            step="any"
            max="100"
            onWheel={(e) => e.target.blur()}
            onKeyDown={(e) => ['ArrowUp', 'ArrowDown'].includes(e.key) && e.preventDefault()}
          />
          {percentage && <label htmlFor="percentage">Percentage</label>}
        </div>

        <div className="floating-select-form" style={{ width: '98%' }}>
          <input
            type="number"
            name="maxUsagePerUser"
            className={`walkin-form ${maxUsagePerUser ? 'has-value' : ''}`}
            value={maxUsagePerUser}
            onChange={(e) => setMaxUsagePerUser(e.target.value)}
            placeholder="Enter Maximum Usage per User"
            required
            min="1"
            onWheel={(e) => e.target.blur()}
            onKeyDown={(e) => ['ArrowUp', 'ArrowDown'].includes(e.key) && e.preventDefault()}
          />
          {maxUsagePerUser && <label htmlFor="maxUsagePerUser">Maximum Usage per User</label>}
        </div>

        <div className="floating-select-form" style={{ width: '98%' }}>
          <input
            type="text"
            name="couponCode"
            className={`walkin-form ${couponCode ? 'has-value' : ''}`}
            value={couponCode}
            onChange={(e) => setCouponCode(e.target.value)}
            placeholder="Enter Coupon Code"
            minLength={6}
            maxLength={10}
            required
          />
          {couponCode && <label htmlFor="couponCode">Coupon Code</label>}
        </div>

        <div className="floating-select-form" style={{ width: '98%' }}>
          <input
            type="date"
            name="startTime"
            className={`walkin-form date-picker-input has-value`}
            value={startTime}
            onChange={(e) => setStartTime(e.target.value)}
            onClick={(e) => e.target.showPicker()}
            required
          />
          <label htmlFor="startTime">Start Date</label>
        </div>

        <div className="floating-select-form" style={{ width: '98%' }}>
          <input
            type="date"
            name="endTime"
            className="walkin-form date-picker-input has-value"
            value={endTime}
            onChange={(e) => setEndTime(e.target.value)}
            onClick={(e) => e.target.showPicker()}
            required
          />
          <label htmlFor="endTime">End Date</label>
        </div>

        <div className="floating-select-form" style={{ width: '98%' }}>
          <Select
            isMulti
            name="scope"
            options={scopeOptions}
            value={selectedScope}
            onChange={handleScopeChange}
            placeholder="Select Scope"
            closeMenuOnSelect={false}
            className={`walkin-form-dropdown ${selectedScope?.length ? 'has-value' : ''}`}
            classNamePrefix="walkin-select"
            menuPortalTarget={document.body}
            styles={{
              control: (provided) => ({ ...provided, padding: '5px' }),
              placeholder: (provided) => ({ ...provided, color: 'black' }),
              menuPortal: (base) => ({ ...base, zIndex: 9999 }),
            }}
          />
          {selectedScope?.length > 0 && <label htmlFor="scope">Select Scope</label>}
        </div>

        <div className="floating-select-form" style={{ width: '98%' }}>
          <Select
            isMulti
            name="sport"
            options={sportOptions}
            value={selectedSport}
            onChange={handleSportChange}
            placeholder="Select Sport"
            closeMenuOnSelect={false}
            className={`walkin-form-dropdown ${selectedSport?.length ? 'has-value' : ''}`}
            classNamePrefix="walkin-select"
            menuPortalTarget={document.body}
            styles={{
              control: (provided) => ({ ...provided, padding: '5px' }),
              placeholder: (provided) => ({ ...provided, color: 'black' }),
              menuPortal: (base) => ({ ...base, zIndex: 9999 }),
            }}
          />
          {selectedSport?.length > 0 && <label htmlFor="sport">Select Sport</label>}
        </div>

        <div className="floating-select-form" style={{ width: '98%' }}>
          <Select
            isMulti
            name="academy"
            options={academyOptions}
            value={selectedAcademy}
            onChange={handleAcademyChange}
            placeholder="Select Academy"
            closeMenuOnSelect={false}
            className={`walkin-form-dropdown ${selectedAcademy?.length ? 'has-value' : ''}`}
            classNamePrefix="walkin-select"
            menuPortalTarget={document.body}
            styles={{
              control: (provided) => ({ ...provided, padding: '5px' }),
              placeholder: (provided) => ({ ...provided, color: 'black' }),
              menuPortal: (base) => ({ ...base, zIndex: 9999 }),
            }}
          />
          {selectedAcademy?.length > 0 && <label htmlFor="academy">Select Academy</label>}
        </div>

        <div className="floating-select-form" style={{ width: '98%' }}>
          <Select
            isMulti
            name="existingcoupon"
            options={ExistingCouponOptions}
            value={existingcoupon}
            onChange={handleExistingCoupon}
            placeholder="Select Existing Coupon"
            closeMenuOnSelect={false}
            className={`walkin-form-dropdown ${existingcoupon?.length ? 'has-value' : ''}`}
            classNamePrefix="walkin-select"
            menuPortalTarget={document.body}
            styles={{
              control: (provided) => ({ ...provided, padding: '5px' }),
              placeholder: (provided) => ({ ...provided, color: 'black' }),
              menuPortal: (base) => ({ ...base, zIndex: 9999 }),
            }}
          />
          {existingcoupon?.length > 0 && <label htmlFor="existingcoupon">Existing Coupon</label>}
        </div>

        <div className="floating-select-form" style={{ width: '98%' }}>
          {mobileNumbers.length > 0 && mobileNumbers.map((number, index) => (
            <div key={index} className="add-more-container" style={{ marginBottom: '8px' }}>
              <div className="floating-select-form" style={{ marginRight: '0px' }}>
                <input
                  type="text"
                  className={`walkin-form ${number ? 'has-value' : ''}`}
                  value={number}
                  onChange={(e) => handleMobileNumberChange(index, e.target.value)}
                  placeholder="Enter Mobile Number"
                  maxLength={10}
                />
                {number && <label htmlFor={`mobileNumber-${index}`}>Mobile Number</label>}
              </div>

              <button type="button" className="remove-btn-mobile" onClick={() => handleRemoveMobileNumber(index)}>
                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" fill="none" viewBox="0 0 24 24" stroke="white" strokeWidth="2">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 7h12M9 7V5a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2M10 11v6M14 11v6M5 7h14l-1 12a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 7z" />
                </svg>
              </button>
            </div>
          ))}

          <div className="add-more-button-container" style={{ display: 'flex', alignItems: 'center', gap: '6px', marginTop: '8px' }}>
            <button type="button" className="plusButton" onClick={handleAddMobileNumber} title="Add Mobile Number">
              <svg className="plusIcon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 30 30">
                <g><path d="M13.75 23.75V16.25H6.25V13.75H13.75V6.25H16.25V13.75H23.75V16.25H16.25V23.75H13.75Z"></path></g>
              </svg>
            </button>
            <span style={{ fontSize: '14px', color: '#555' }}>Add Mobile Number</span>
          </div>
        </div>

        <div className="floating-select-form" style={{ width: '98%' }}>
          <select
            name="forAllUsers"
            className={`walkin-form ${forAllUsers && forAllUsers !== 'none' ? 'has-value' : ''}`}
            value={forAllUsers}
            onChange={(e) => setForAllUsers(e.target.value)}
            required
          >
            <option value="none" disabled>Select an option</option>
            <option value="yes">Yes</option>
            <option value="no">No</option>
          </select>
          {forAllUsers && forAllUsers !== 'none' && <label htmlFor="forAllUsers">Apply to All Users</label>}
        </div>

        <label className="checkbox-label" style={{ width: '110px' }}>
          Is Hidden:
          <input
            type="checkbox"
            name="isHidden"
            checked={isHidden}
            onChange={(e) => setIsHidden(e.target.checked)}
            className="checkbox-input"
          />
        </label>

        {isSuperAdmin &&
          <label className="checkbox-label" style={{ width: '130px' }}>
            For admins:
            <input
              type="checkbox"
              name="forAdminOnly"
              checked={forAdminsOnly}
              onChange={(e) => setForAdminsOnly(e.target.checked)}
              className="checkbox-input"
            />
          </label>
        }

        <div className="form-actions">
          <button type="submit" className="submit-button">
            {isEditMode ? 'Update Coupon' : 'Create Coupon'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default NewCoupon;
